import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Only DateTimeUtils should ever import this class directly!
// There is NO backward compatibility support for this module.

// Mostly holds luxon-related implementations of key DateUtils methods

import { DateTimeFormatterAndParser, DateTimeParseContextImpl } from "abweb/utils/date/DateTimeStructs";
import { amPmParseStringPreprocessor, commaDateTimeSeparatorPreprocessor, dateSeparatorPreprocessor, DateTimeParserChain, NARROW_SPACE_GLOBAL, narrowToNormalSpacePreprocessor } from "abweb/utils/date/internal/DateTimeParserChain";
import { createDateTimeParseError, preParseValue, shouldFixupParseStringWhitespace } from "abweb/utils/date/internal/DateTimeParserHelper";
import { ErrorUtils } from "abweb/utils/ErrorUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { DateTime } from "luxon";
const LOG = LogUtils.getLogger(__filename);

// Package scoped (used by multiple classes within this file */
let LuxonDateTimeOptsDefaults; // used in parsing

export class DateUtilsLuxonInternals {
  constructor() {
    // static helper class
  }

  // Called by both DateUtils and DateTimeUtils onLocaleChanged methods, as both depend on it.
  static onLocaleChange(newDatetimeLocale) {
    if (DateUtilsLuxonInternals.datetimeLocale === newDatetimeLocale) {
      return;
    }
    DateUtilsLuxonInternals.datetimeLocale = newDatetimeLocale;
    DateUtilsLuxonInternals.luxonLocaleOpts = {
      locale: newDatetimeLocale
    }; // used in formatting

    // If you specify a zone, it is an 'IANA Zone' which is calculated with more precision
    // then not specifying zone (results in a 'System Zone').
    // An IANA zone has more precision (does not suffer from 'seconds offset' issue documented in ABSDK-5177)
    // but is a bit slower.  Given that we tend on only use these parsers for parsing interactive edits,
    // we prefer accuracy (and performance of IANA parser will be more than fast enough!)
    // Note that if factory Intl.
    const defaultTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    LuxonDateTimeOptsDefaults = {
      locale: newDatetimeLocale,
      zone: defaultTZ
    };
    DateUtilsLuxonInternals.TOKEN_AM = DateTime.fromJSDate(DateUtilsLuxonInternals.OneAMTime).toFormat("a", DateUtilsLuxonInternals.luxonLocaleOpts);
    DateUtilsLuxonInternals.TOKEN_PM = DateTime.fromJSDate(DateUtilsLuxonInternals.TwoPMTime).toFormat("a", DateUtilsLuxonInternals.luxonLocaleOpts);
  }
  static get localizedAM() {
    return DateUtilsLuxonInternals.TOKEN_AM;
  }
  static get localizedPM() {
    return DateUtilsLuxonInternals.TOKEN_PM;
  }

  ////// Luxon-based formatting (where they provide an explicit luxon pattern like MM/dd/yyyy)

  // Note formatDateTime was only used by DateUtilsLuxonInternalsUSpec to moved to uspec

  static buildDateTimeFormatter(luxonFormat) {
    return new LuxonDateTimeFormatter(luxonFormat);
  }

  ////// Luxon-based parsing (where they provide an explicit luxon pattern like MM/dd/yyyy)

  static buildDateTimeParser(luxonParseFormat, dateTimeOptions = {}) {
    // ABSDK-2088: if intl format has narrow spaces, convert to normal spaces (which are what user typically enters via keyboard)
    // as Luxon requires 'exact format match'.  This way can parse user input.
    const luxonParseFormatFixed = luxonParseFormat.replace(NARROW_SPACE_GLOBAL, " ");
    const hadNarrowSpace = luxonParseFormat !== luxonParseFormatFixed;
    const primaryParser = new LuxonDateTimeParser(luxonParseFormatFixed, dateTimeOptions);

    // Prefer adding preprocessor over adding secondary parsers, as the secondary parsers are independent
    // while the preprocessors are cumulative
    const dateTimeParserChain = new DateTimeParserChain(primaryParser);
    dateSeparatorPreprocessor(dateTimeParserChain);
    commaDateTimeSeparatorPreprocessor(dateTimeParserChain);
    if (hadNarrowSpace) {
      // format originally contained narrow spaces.
      // So if they cut-n-paste, replaces narrow spaces with normal spaces in parse string
      narrowToNormalSpacePreprocessor(dateTimeParserChain);
    }
    amPmParseStringPreprocessor(dateTimeParserChain, DateUtilsLuxonInternals.localizedAM, DateUtilsLuxonInternals.localizedPM);
    timeLessPrecisionParser(dateTimeParserChain);
    return dateTimeParserChain.dateTimeParserFactory();
  }

  /**
   * @param luxonFormat luxon format string used to format a Date as a string.
   * @param luxonParseFormat luxon parse format string used to parse a string as a Date(| time | datetime).
   * Defaults to luxonFormat if not provided as most times the luxonFormat and luxonParseFormat strings are the same
   * (but note that there are luxon format tokens that* aren't valid as parsing tokens).
   * @return instance that can be used for both formatting and parsing of Date instances
   */
  static buildDateTimeFormatterAndParser(luxonFormat, luxonParseFormat) {
    luxonParseFormat = luxonParseFormat ?? luxonFormat;
    const formatter = DateUtilsLuxonInternals.buildDateTimeFormatter(luxonFormat);
    const parser = DateUtilsLuxonInternals.buildDateTimeParser(luxonParseFormat);
    return new DateTimeFormatterAndParser(formatter, parser);
  }

  ///////// Kendo Format Support //////////////
  /**
   * This converts a kendo format string to an equivalent Luxon format string.
   * It ONLY supports the Kendo tokens documented in:
   * https://docs-stage.abinitio.com/r/416/Metadata_Hub/Metadata_Hub_Reporters_Guide/PART_TWO_Creating_reports/Inserting_and_changing_columns/Changing_column_grouping_name_appearance_and_formatting_of_special_values/Formatting_columns_containing_datetime_values
   *
   * @param kendoFormat
   * @returns luxonFormat Luxon format string equivalent to kendoFormat arg
   * @throws KendoToLuxonFormatConversionException if the kendoFormat can not be converted to an equivalent luxon format
   */
  static convertToLuxonFormat(kendoFormat) {
    if (DateUtilsLuxonInternals.SupportedKendoTokenSubset.test(kendoFormat)) {
      try {
        // Luxon has the same tokens for M, MM, MMM, MMMM, d, dd, h, hh, H, HH, m, mm, s, ss, yy, yyyy
        // Go from longest to shortest on match length
        const luxonFormat = kendoFormat.replace(/dddd/g, "EEEE") // localized day of week full 'Wednesday'
        .replace(/ddd/g, "EEE") // localized day of week abbr 'Wed'
        .replace(/fff/g, "u") // msecs, padded to 3 '054'
        // timezone offset NOT technically part of ab initio supported subset, but it is only kendo custom token not supported so add it
        // Kendo custom tokens: https://docs.telerik.com/kendo-ui/globalization/intl/dateformatting#custom-date-formats
        .replace(/zzz/g, "ZZ") // time offset "-05:00"
        .replace(/ff/g, "uu") // hundredths secs, padded to 2 '05'
        .replace(/tt/g, "a") // localized AM/PM indicator  'AM'
        .replace(/f/g, "uuu"); // tenths secs,  '1'
        return luxonFormat;
      } catch (err) {
        LOG.error(`Error while converting kendo format '${kendoFormat}' to Luxon format: ${ErrorUtils.getErrorMsgFromObj(err)}`);
        // fall through to below throw of KendoToLuxonFormatConversionException
      }
    }
    throw new KendoToLuxonFormatConversionException(`Can not convert following Kendo format to equivalent Luxon format: '${kendoFormat}'`);
  }
  static convertLuxonDateTimeParseFormatToKendoParseFormat(dtParser) {
    // defensive
    if (dtParser.parseContext.parseGrammarName !== "luxon") {
      throw new Error(`Expect parse grammar of 'luxon' but received ${dtParser.parseContext.parseGrammarName}`);
    }
    // for datetimes, main diff is AM/PM is 'a' for luxon parse format, and 'tt' for kendo
    let kendoFormat = dtParser.parseContext.parseFormat.replace("a", "tt") // AM or PM
    .replace("EEEE", "dddd") // full day of week Thursday
    // ensure minutes and seconds always have leading zero (noon is 12:00:00 and not 12:0:0)
    .replace(/:m(?!m)/, ":mm").replace(/:s(?!s)/, ":ss");
    if (["fr", "zh-CN"].includes(DateUtilsLuxonInternals.datetimeLocale)) {
      // french and chinese has leading zero for the hour (01:02:03)
      kendoFormat = kendoFormat.replace(/H(?!H)/, "HH");
    }
    return kendoFormat;
  }
}
_defineProperty(DateUtilsLuxonInternals, "OneAMTime", new Date(1970, 0, 1, 1));
_defineProperty(DateUtilsLuxonInternals, "TwoPMTime", new Date(1970, 0, 1, 14));
// Supported subset of token characters, including supported whitespace, that matches exacly what is documented in:
// https://docs-stage.abinitio.com/r/416/Metadata_Hub/Metadata_Hub_Reporters_Guide/PART_TWO_Creating_reports/Inserting_and_changing_columns/Changing_column_grouping_name_appearance_and_formatting_of_special_values/Formatting_columns_containing_datetime_values
_defineProperty(DateUtilsLuxonInternals, "SupportedKendoTokenSubset", /^(?:[ ,:.\\/\-]|[dfhHmMsy]|tt|yy|zzz)*$/);
_defineProperty(DateUtilsLuxonInternals, "datetimeLocale", void 0);
_defineProperty(DateUtilsLuxonInternals, "luxonLocaleOpts", void 0);
// used in formatting
// private static luxonDateTimeOptsDefaults: DateTimeOptions; // used in parsing
_defineProperty(DateUtilsLuxonInternals, "TOKEN_AM", "AM");
_defineProperty(DateUtilsLuxonInternals, "TOKEN_PM", "PM");
export class KendoToLuxonFormatConversionException extends Error {}

/*private*/
class LuxonDateTimeFormatter {
  constructor(luxonFormat) {
    this.luxonFormat = luxonFormat;
  }
  format(value) {
    if (value) {
      return DateTime.fromJSDate(value).toFormat(this.luxonFormat, DateUtilsLuxonInternals.luxonLocaleOpts);
    }
    throw new Error("Can not format a date as received a null or undefined value.");
  }
}

/*private*/
class LuxonDateTimeParser {
  constructor(parseFormat, dateTimeOptions = {}) {
    this.dateTimeOptions = {
      ...LuxonDateTimeOptsDefaults,
      ...dateTimeOptions
    };
    this.parseContext = new LuxonDateTimeParseContext(parseFormat);
    this.fixupWhitespace = shouldFixupParseStringWhitespace(this.parseContext.parseFormat);
  }
  parse(val) {
    const fixedVal = preParseValue(val, this.fixupWhitespace);
    if (fixedVal) {
      /** Caution: responsibility of callers to check isValid prop on returned Luxon DateTime instance! */
      const luxonDateTime = DateTime.fromFormat(fixedVal, this.parseContext.parseFormat, this.dateTimeOptions);
      if (luxonDateTime?.isValid) {
        return luxonDateTime.toJSDate();
      }
    }
    // Throw if val falsey (null, undefined, empty string) or if luxon unable to parse the non-empty string
    // Possible enhancement: Also a dt.invalidReason that could show
    return createDateTimeParseError(val, this.parseContext);
  }
}
class LuxonDateTimeParseContext extends DateTimeParseContextImpl {
  constructor(parseFormat) {
    super("luxon", parseFormat);
  }
}

// The 'less precision time format' must have at least hours and mins, as nothing to trim if it is just hours
const TIME_FULL_FORMAT = /(hh|HH|h|H):(mm|m)(:(ss|s)(\.(SSS|uuu|uu|u|S))?)?/;
const TIME_FULL_FORMAT_GLOBAL = new RegExp(TIME_FULL_FORMAT, "g"); // use in 'count'

function timeLessPrecisionParser(dtpChain) {
  const luxonParseFormat = dtpChain.parseContext.parseFormat;
  const numTimeStrings = StringUtils.countSubstrings(luxonParseFormat, TIME_FULL_FORMAT_GLOBAL);
  if (numTimeStrings !== 1) return; // below forgiveness only supports a single time format in parse format

  // seconds and fractional seconds groups are optional
  let timeFormatMatch = luxonParseFormat.match(TIME_FULL_FORMAT);
  if (!timeFormatMatch) return; // defensive, should never happen if numTimeStrings=1
  const wholeTimeFormat = timeFormatMatch[0];
  const hourFormat = timeFormatMatch[1];
  const minFormat = timeFormatMatch[2];
  const optionalSecFormat = timeFormatMatch[4]; // 3 is ':ss', while 4 is 'ss'
  const optionalMSecFormat = timeFormatMatch[6]; // 5 is '.SSS', while 6 is 'SSS'

  // Only do the 'most significant trimming' as the secondary parser (which builds its own forgiveness
  // rules based on its own unique parseFormat) will then trim it further (recursive)
  const trimmedPrecisionFormat = optionalMSecFormat ? `${hourFormat}:${minFormat}:${optionalSecFormat}` // "h:mm:ss"
  : optionalSecFormat ? `${hourFormat}:${minFormat}` // "h:mm"
  : hourFormat;
  const lessPrecisionParseFormat = luxonParseFormat.replace(wholeTimeFormat, trimmedPrecisionFormat);
  dtpChain.addSecondaryParser(DateUtilsLuxonInternals.buildDateTimeParser(lessPrecisionParseFormat));
}

// Outside of DateUtilsLuxonInternals class to guarantee that it is 'last' (even when intellij reorders static class members!)
// noinspection JSUnusedLocalSymbols
// const staticInitializer: void =
DateUtilsLuxonInternals.onLocaleChange(new Intl.DateTimeFormat().resolvedOptions().locale);
//# sourceMappingURL=DateUtilsLuxonInternals.js.map
